import { action, computed, observable } from 'mobx'
import type {
  AlertErrorHandlerFactory,
  AlertFactory,
  AlertRetryWrapperFactory,
} from '@/features/shell'
import type { NavigateFn } from '@/RootStore'
import type {
  CreateCustomerFn,
  CreateMemberManuallyFn,
  FetchMemberFn,
  ListUserWorkspaceMembershipsFn,
  FetchWorkspaceFn,
  GetCurrentUserFn,
  GetCurrentUserWorkspaceMembershipsFn,
  GetUserDataFn,
  SearchMembersFn,
} from '@/features/iam'
import type {
  EmailCheckoutLinkFn,
  GenerateCheckoutLinkFn,
  InvalidateCheckoutSessionsFn,
} from '@/features/billing'
import type {
  ApplyJobCouponByCodeFn,
  PublishedLegendFn,
  JobCheckoutViewModelFactory,
  RemoveJobCouponByCodeFn,
  SearchJobsFn,
  SubmitJobRequestFn,
} from '@/features/jobs'
import type { SearchCallCenterContactsFn } from '@/features/freshbooks'
import { type JobCreationRootViewModel } from '@/features/jobs/drafting/JobCreationRootViewModel'
import { CallCenterCustomerLookupViewModel } from './CallCenterCustomerLookupViewModel'
import { filterCallCenterMemberships } from './call-center-util'
import type { CallCenterMembershipItem } from './CallCenterSelectionViewModel'
import { CallCenterJobViewModel } from '@/features/call-center/JobSection/CallCenterJobViewModel'
import { CallCenterAddClientDialogViewModel } from '@/features/call-center/AddClientDialog/CallCenterAddClientDialogViewModel'
import { links } from '@/utils/link'
import { Id } from '@/infra/id'
import type { ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'

export type CallCenterSectionId = 'CustomerLookup' | 'Job' | 'Checkout'

export interface CallCenterSection {
  id: CallCenterSectionId
  title: string
}

export class CallCenterViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  readonly customerLookupVm: CallCenterCustomerLookupViewModel

  readonly addClientDialogVm: CallCenterAddClientDialogViewModel

  readonly jobVm: CallCenterJobViewModel

  /**
   * The last job that was opened. Kept after leaving the job page, so the
   * breadcrumbs can navigate back to it.
   */
  @observable accessor jobId: Id = Id.none

  constructor(
    searchCallCenterContacts: SearchCallCenterContactsFn,
    searchMembers: SearchMembersFn,
    fetchMember: FetchMemberFn,
    createMemberManually: CreateMemberManuallyFn,
    createCustomer: CreateCustomerFn,
    searchJobs: SearchJobsFn,
    emailCheckoutLink: EmailCheckoutLinkFn,
    private readonly _getCurrentUser: GetCurrentUserFn,
    private readonly _getUserData: GetUserDataFn,
    getCurrentUserWorkspaceMemberships: GetCurrentUserWorkspaceMembershipsFn,
    listUserWorkspaceMemberships: ListUserWorkspaceMembershipsFn,
    fetchWorkspace: FetchWorkspaceFn,
    submitJobRequest: SubmitJobRequestFn,
    applyJobCouponByCode: ApplyJobCouponByCodeFn,
    removeJobCouponByCode: RemoveJobCouponByCodeFn,
    publishedLegend: PublishedLegendFn,
    jobCreationRoot: JobCreationRootViewModel,
    createJobCheckoutViewModel: JobCheckoutViewModelFactory,
    generateCheckoutLink: GenerateCheckoutLinkFn,
    invalidateCheckoutSessions: InvalidateCheckoutSessionsFn,
    newAlert: AlertFactory,
    createAlertErrorHandler: AlertErrorHandlerFactory,
    createAlertRetryWrapper: AlertRetryWrapperFactory,
    private readonly _navigate: NavigateFn,
  ) {
    this.customerLookupVm = new CallCenterCustomerLookupViewModel(
      searchCallCenterContacts,
      searchMembers,
      getCurrentUserWorkspaceMemberships,
      listUserWorkspaceMemberships,
      fetchWorkspace,
      searchJobs,
      publishedLegend,
      createAlertRetryWrapper,
      {
        createJob: (item) => this._requestCreateJob(item),
        inviteToWorkspace: (item) => this._inviteToWorkspace(item),
      },
    )
    this.jobVm = new CallCenterJobViewModel(
      jobCreationRoot,
      createJobCheckoutViewModel,
      submitJobRequest,
      applyJobCouponByCode,
      removeJobCouponByCode,
      generateCheckoutLink,
      invalidateCheckoutSessions,
      emailCheckoutLink,
      newAlert,
      createAlertErrorHandler,
      _navigate,
    )
    this.addClientDialogVm = new CallCenterAddClientDialogViewModel(
      createCustomer,
      fetchMember,
      createMemberManually,
      () => filterCallCenterMemberships(getCurrentUserWorkspaceMemberships()),
      this.jobVm.confirmationDialog.open,
      newAlert,
      createAlertErrorHandler,
    )

    this._lifecycle
      .manage(this.customerLookupVm)
      .manage(this.jobVm)
      .manage(this.addClientDialogVm)
  }

  /**
   * Only admins and call agents can use the call center.
   */
  @computed
  get canAccessCallCenter() {
    const publicId = this._getCurrentUser().publicId
    const userType = publicId
      ? this._getUserData(publicId)?.userDataType
      : undefined
    return userType === 'Admin' || userType === 'CallAgent'
  }

  getBreadcrumbItems(isOnCheckoutPage: boolean): CallCenterSection[] {
    const items: CallCenterSection[] = [
      { id: 'CustomerLookup', title: 'Customer lookup' },
    ]

    if (this.jobVm.jobCreation !== null) {
      items.push({ id: 'Job', title: 'Tax situation' })
    }

    if (isOnCheckoutPage) {
      items.push({ id: 'Checkout', title: 'Checkout' })
    }

    return items
  }

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
  }

  /** @inheritDoc */
  deactivate() {
    this._lifecycle.deactivate()
  }

  /**
   * Sets the job open in the call center.
   */
  @action.bound
  setJobId(jobId: Id) {
    this.jobId = jobId
  }

  @action.bound
  setActiveSection(section: CallCenterSectionId) {
    const jobId = this.jobId

    switch (section) {
      case 'CustomerLookup': {
        this.jobVm.resetActiveJobState()
        this.jobId = Id.none

        this._navigate(links.callCenter())
        break
      }
      case 'Job': {
        if (jobId) {
          this._navigate(links.callCenterJob(jobId))
        }
        break
      }
      case 'Checkout': {
        this._navigate(links.callCenterJobCheckout(jobId))
      }
    }
  }

  /**
   * Opens the add client dialog for the selected client.
   */
  @action.bound
  addSelectedClientToWorkspace() {
    const selectionVm = this.customerLookupVm.callCenterSelectionVm
    const contact = selectionVm.selectedResult?.hubspotContact
    const primaryMember = selectionVm.primaryMember

    this.addClientDialogVm.prefill({
      email: selectionVm.email,
      firstName: contact?.firstName ?? primaryMember?.givenName ?? '',
      lastName: contact?.lastName ?? primaryMember?.familyName ?? '',
      phone: selectionVm.phone || undefined,
    })

    this.addClientDialogVm.show()
  }

  @action.bound
  private _requestCreateJob(item: CallCenterMembershipItem) {
    if (!item.workspace || !item.member) {
      return
    }

    this.jobVm.confirmationDialog.open({
      workspace: item.workspace,
      member: item.member,
    })
  }

  /**
   * Opens the add client dialog for a HubSpot contact that signed up for a
   * workspace's waitlist.
   */
  @action.bound
  private _inviteToWorkspace(item: CallCenterMembershipItem) {
    const contact =
      this.customerLookupVm.callCenterSelectionVm.selectedResult?.hubspotContact
    if (!contact) {
      return
    }

    this.addClientDialogVm.prefill({
      email: contact.email,
      firstName: contact.firstName,
      lastName: contact.lastName,
      phone: contact.phone,
      workspaceId: item.workspaceId,
      workspace: item.workspace,
    })

    this.addClientDialogVm.show()
  }
}
